import { apiErrorMessage } from '@agos/api-client'
import type { OrderListItem } from '@agos/api-client/model'
import {
  getGetOrdersQueryKey,
  postOrdersOrderIdConfirmStatus,
  useGetOrders,
  useGetSalesChannels,
} from '@agos/api-client/orders'
import {
  ALL_CHANNELS,
  ArrivalSoundPicker,
  arrivalStep,
  mayHandOverTakings,
  FiskalStornoAction,
  boardChannelOf,
  boardOrders,
  channelRestaurantPath,
  chimeToggleLabel,
  credentialsNeeded,
  filterChannelsOf,
  matchesSelection,
  onBoard,
  ownBoardChannels,
  sanitiseSelection,
  selectAll,
  selectionNote,
  toggleChannel,
  type ChannelSelection,
  formatMoney,
  readyBellToggleLabel,
  startOfDay,
  T,
  tt,
  useArrivalChime,
  useAuth,
  useReadyBell,
} from '@agos/shared'
import {
  CancelOrderModal,
  BUTTON_GHOST,
  BUTTON_PRIMARY,
  ChannelTabs,
  cn,
  EmptyState,
  PageHeader,
  StatusBadge,
  toast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Calculator,
  Ban,
  BellOff,
  BellRing,
  CheckCheck,
  CircleDot,
  Pencil,
  Plus,
  Receipt,
  RefreshCw,
  ShoppingBag,
  Store,
  Users,
  Volume2,
  VolumeX,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { ChannelBoardGone, ElsewhereNote } from '../channel-boards/ChannelBoardGone'
import { WaiterHandoverModal } from '../handover/WaiterHandoverModal'
import { useLocation, useSearch } from 'wouter'


import { orderStatusColor, orderStatusLabel } from '../order-status'
import { mayOpenOrderEditor } from '../order-rights'
import { openRoomOrders, roomFilterChannels } from '../table-map/table-status'
import { useKitchenCalls } from '../kitchen-calls/use-kitchen-calls'
import { PaymentDialog, PAYMENT_POLL_MS } from './PaymentDialog'
import { statusMemoryOf, turnedReady, type StatusMemory } from './ready-bell'
import {
  needsReceiptStorno,
  noteOf,
  openForNote,
} from './rounds'
import {
  isWaitingToBeCarried,
  SERVED,
  allServed,
  effectiveServed,
  toggledFrom,
  useServedLines,
  type PendingTick,
  type ServableLine,
} from './served-lines'

/**
 * `/restaurant` — the till, ported from legacy `pages/restaurant.tsx`.
 *
 * The room's open tables, and the two things a waiter does with one: add to it
 * or settle it. Adding goes to the order builder with the table already chosen;
 * settling opens the payment dialog, which can take the whole bill or split it.
 *
 * §14.1 — the board polls every eight seconds, which is what legacy uses while
 * a payment dialog is open. A table cancelled in the kitchen while a waiter is
 * mid-payment has to surface before they take the money.
 */

const REFRESH_MS = PAYMENT_POLL_MS
const ORDERS_PAGE_SIZE = '500'

export function RestaurantPage({
  boardChannel = null,
}: {
  /**
   * §9.688, owner #24 (O79) - a channel's own *Otvorené stoly* (the terrace's *OFYR*), or `null` for the ordinary
   * one. Which orders each draws is `onBoard`'s rule in `@agos/shared`; absent is `/restaurant` exactly as it was.
   */
  boardChannel?: string | null
} = {}) {
  const queryClient = useQueryClient()
  const [, navigate] = useLocation()
  const search = useSearch()

  const [payingId, setPayingId] = useState<number | null>(() => {
    const requested = new URLSearchParams(search).get('pay')
    return requested ? Number(requested) : null
  })
  const [cancellingId, setCancellingId] = useState<number | null>(() => {
    const requested = new URLSearchParams(search).get('cancel')
    return requested ? Number(requested) : null
  })

  const today = startOfDay(new Date()).toISOString()
  const { data: result, isLoading } = useGetOrders(
    { date_from: today, page_size: ORDERS_PAGE_SIZE },
    { query: { refetchInterval: REFRESH_MS } },
  )

  // §9.126 — one clock for every card, advanced on the same cadence the list refreshes on. Reading
  // `Date.now()` inside a card would be an impure call during render and would also let two cards
  // disagree about what time it is.
  // §9.127 — one writer for both this screen and the table-map panel.
  const { ticks, isMarkingAll, setServed, markAllServed } = useServedLines(() => {
    void queryClient.invalidateQueries({ queryKey: getGetOrdersQueryKey() })
  })

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), REFRESH_MS)
    return () => clearInterval(tick)
  }, [])
  const orders = useMemo(() => result?.data ?? [], [result])
  // §9.653 - the cancel asks a permission, not a role, so the prompt needs the reader too.
  const { user, hasPermission } = useAuth()
  /* §9.744, owner O114 - a card's *Upraviť* opens the order in the editor, whose save asks `edit_order`
     (BACKEND.md §10A.397) and whose route asks `new_order` (§9.744 amended). Read once, so every card answers the same
     question. */
  const mayEditOrder = mayOpenOrderEditor(hasPermission)
  // §9.444, FB-767 - the same sheet TableMapPage opens.
  const [showHandover, setShowHandover] = useState(false)
  const chime = useArrivalChime()
  const previousTableCount = useRef<number | null>(null)

  /**
   * One card per occupied table. Reuses the floor plan's mapping, so the two
   * screens can never disagree about which order belongs to which table.
   */
  // §9.393 - the counter half of this list asks the channel table what a counter collection is,
  // rather than spelling `bar`. Same hook the dispatch board uses, same cached query.
  const { data: salesChannels, isError: channelsFailed } = useGetSalesChannels()
  const channels = useMemo(() => filterChannelsOf(salesChannels), [salesChannels])
  // §9.688 - this board's orders only: a channel's own, or everything except the channels that have their own.
  const openTables = useMemo(
    () => openRoomOrders(boardOrders(orders, boardChannel, channels), tt('Osobný odber'), channels),
    [orders, channels, boardChannel],
  )
  const board = boardChannelOf(boardChannel, channels)
  /* §9.689, owner #24 (O79c) - the kitchen's channel filter, on the room: the same tabs and the same rules, and the
     selection survives the poll but not the session (`channel-filter.ts`). Only the channels this screen can draw,
     and only the ones this board draws - a channel's own board has one, so its strip is not drawn. */
  const [channelSelection, setChannelSelection] = useState<ChannelSelection>(ALL_CHANNELS)
  const roomSlugs = useMemo(() => openTables.map(({ order }) => order.type ?? ''), [openTables])
  const roomChannels = useMemo(
    () =>
      roomFilterChannels(channels, roomSlugs).filter((channel) =>
        onBoard(channel.slug, boardChannel, channels),
      ),
    [channels, roomSlugs, boardChannel],
  )
  const roomSelection = sanitiseSelection(channelSelection, roomChannels)
  const shownTables = useMemo(
    () => openTables.filter(({ order }) => matchesSelection(order.type ?? '', roomSelection)),
    [openTables, roomSelection],
  )
  // Counted over the whole room, not the filtered one: a tab says what tapping it would bring.
  const roomCounts = useMemo(() => {
    const tally: Record<string, number> = {}
    for (const slug of roomSlugs) if (slug !== '') tally[slug] = (tally[slug] ?? 0) + 1
    return tally
  }, [roomSlugs])
  // §9.688 - on the ordinary board, the channels that have their own and how many open bills each is holding.
  const elsewhere = useMemo(
    () =>
      boardChannel === null
        ? ownBoardChannels(channels).map((channel) => ({
            name: channel.name,
            href: channelRestaurantPath(channel.slug),
            count: openRoomOrders(boardOrders(orders, channel.slug, channels), '', channels).length,
          }))
        : [],
    [orders, channels, boardChannel],
  )

  const refresh = () => void queryClient.invalidateQueries({ queryKey: getGetOrdersQueryKey() })

  /**
   * A table the room did not have a moment ago — §9.110.
   *
   * <p>This screen had no arrival tracking of its own; it simply redrew. The event a waiter cares
   * about is a **new table opening**, not an order changing on one they are already serving, so the
   * count of open tables is what is watched. `null` until the first load, so arriving at a room with
   * six tables already open does not announce six.
   *
   * <p><b>Gentle, like the kitchen.</b> `urgent` stays reserved for dispatch's unaccepted web order;
   * a table opening is the normal event of a service.
   */
  useEffect(() => {
    // FB-997, §9.677 - remembered from the first answered read, not from the loading render's empty room.
    const step = arrivalStep(previousTableCount.current, openTables.length, result !== undefined)
    previousTableCount.current = step.baseline
    if (step.arrived > 0) {
      chime.play('gentle')
    }
  }, [openTables.length, chime, result])

  /**
   * The kitchen's *Hotová*, heard here - §9.675, owner O56, legacy's `playReadySound`.
   *
   * <p>Each order's status is remembered from one answered read to the next, and the bell rings once when any of the
   * orders this screen draws has moved into `quality_check` since - `turnedReady` is the rule. **Nothing is
   * remembered until the read has answered** (FB-997, §9.677): the render before it has no orders, and remembering
   * that would make every waiting order look as though it had just turned ready.
   */
  const bell = useReadyBell()
  const lastStatuses = useRef<StatusMemory | null>(null)
  useEffect(() => {
    if (result === undefined) return
    const drawn = openTables.map((entry) => entry.order)
    if (turnedReady(lastStatuses.current, drawn).length > 0) bell.ring()
    lastStatuses.current = statusMemoryOf(drawn)
  }, [result, openTables, bell])

  // §9.720, owner O95 - the kitchen's zvonček rings here too, under the bell's own switch above.
  useKitchenCalls()

  const paying = orders.find((order) => order.id === payingId) ?? null
  const cancelling = orders.find((order) => order.id === cancellingId) ?? null

  const cancelOrder = async (email: string, password: string, reason: string) => {
    if (!cancelling) return
    await postOrdersOrderIdConfirmStatus(cancelling.id ?? 0, {
      newStatus: 'cancelled',
      reason,
      // Its own optimistic-concurrency check, and nothing to do with who is acting.
      expectedStatus: cancelling.status,
      // §9.344 - sent only when the dialog collected them. `OrderActorResolver.resolve` tests for
      // **null**, so a blank pair is refused rather than falling back to the signed-in employee.
      ...(email.trim() === '' ? {} : { email: email.trim(), password }),
    })
    setCancellingId(null)
    refresh()
    toast({
      title: tt('Objednávka zrušená'),
      description: `#${cancelling.orderNumber}`,
    })
  }

  /* §9.688 - a channel's open tables whose channel no longer has its own boards (switched off for the season, flag
     unticked, a typed URL), or whose channel list could not be read: its bills are on the ordinary Otvorené stoly. */
  if (boardChannel !== null && (channelsFailed || (salesChannels !== undefined && board === null))) {
    return (
      <div className="space-y-4">
        <PageHeader title={tt('Otvorené stoly')} icon={<Store className="w-5 h-5" />} />
        <ChannelBoardGone failed={channelsFailed} homeHref="/restaurant" homeLabel={tt('Otvorené stoly')} />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <PageHeader
        // §9.688 - the channel's name on its own board, so a tablet on the terrace says whose tables these are.
        title={board === null ? tt('Otvorené stoly') : `${tt('Otvorené stoly')} ${board.name}`}
        description={tt('Klikni na stôl pre pridanie alebo zaplatenie.')}
        icon={<Store className="w-5 h-5" />}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={chime.toggle}
              aria-label={chimeToggleLabel(chime.enabled)}
              data-sound-toggle={chime.enabled ? 'on' : 'off'}
              className={cn(BUTTON_GHOST, 'p-2 rounded-lg')}
            >
              {chime.enabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            {/* §9.676, owner O57 - which sound a new table makes on this device. Not the bell's: that one is
              * legacy's ding-dong and not a choice, so the two can never come to sound alike. */}
            <ArrivalSoundPicker chime={chime} className={cn(BUTTON_GHOST, 'p-2 rounded-lg')} />
            {/* §9.675 - the kitchen's bell has its own switch, as legacy's did. `data-ready-bell` and not
              * `data-sound-toggle`: that marker is the new-order chime's, one per screen. */}
            <button
              type="button"
              onClick={bell.toggle}
              aria-label={readyBellToggleLabel(bell.enabled)}
              title={readyBellToggleLabel(bell.enabled)}
              data-ready-bell={bell.enabled ? 'on' : 'off'}
              className={cn(BUTTON_GHOST, 'p-2 rounded-lg')}
            >
              {bell.enabled ? <BellRing className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={refresh}
              aria-label={tt('Obnoviť')}
              className={cn(BUTTON_GHOST, 'p-2 rounded-lg')}
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            {/* §9.444, FB-767 - *"add also to Otvorené stoly next to Nová objednávka"*. A waiter
              * working from this screen had to open the floor plan to hand over their takings.
              * Same control, role list and modal as `TableMapPage`, with the list moved to
              * `@agos/shared` rather than copied: a second screen is how a role list starts
              * disagreeing with itself. */}
            {mayHandOverTakings(user?.systemRole) && (
              <button
                type="button"
                data-handover
                onClick={() => setShowHandover(true)}
                title={tt('Odovzdať tržbu (uzávierka zmeny)')}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 text-xs font-bold transition-colors"
              >
                <Calculator className="w-3.5 h-3.5" /> <T>Odovzdať tržbu</T>
              </button>
            )}
            <button
              type="button"
              // §9.688 - from a channel's own board the new order is placed on that channel, so it lands there.
              data-new-order-channel={board?.slug ?? 'restaurant'}
              onClick={() => navigate(`/new-order?type=${board?.slug ?? 'restaurant'}`)}
              className={cn('flex items-center gap-1.5 px-3 py-2 rounded-lg', BUTTON_PRIMARY, 'text-xs')}
            >
              <Plus className="w-3.5 h-3.5" /> <T>Nová objednávka</T>
            </button>
          </div>
        }
      />

      {/* §9.688 - the channels whose bills this board no longer draws, and where they are. */}
      <ElsewhereNote entries={elsewhere} />

      {/* §9.689 - the kitchen's channel tabs. `ChannelTabs` draws nothing for fewer than two channels. */}
      {roomChannels.length > 1 && (
        <div className="space-y-1.5" data-room-channel-filter>
          <ChannelTabs
            channels={roomChannels}
            selection={roomSelection}
            counts={roomCounts}
            onToggle={(slug) => setChannelSelection(toggleChannel(slug, roomSelection))}
            onAll={() => setChannelSelection(selectAll())}
          />
          {/* A narrowed room must not read as an empty one. */}
          {selectionNote(roomSelection, roomChannels, shownTables.length, openTables.length) !== '' && (
            <p className="text-xs text-muted-foreground" data-channel-note>
              {selectionNote(roomSelection, roomChannels, shownTables.length, openTables.length)}
            </p>
          )}
        </div>
      )}

      {isLoading || (boardChannel !== null && salesChannels === undefined) ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[1, 2, 3].map((placeholder) => (
            <div
              key={placeholder}
              className="h-40 rounded-2xl bg-muted/40 border border-border animate-pulse"
            />
          ))}
        </div>
      ) : openTables.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-12">
          <EmptyState
            icon={<Store className="w-10 h-10" />}
            title={tt('Žiadne otvorené stoly')}
            description={tt('Nová objednávka otvorí stôl.')}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {shownTables.map(({ key, heading, order, isPickup }) => (
            <TableCard
              key={key}
              tableName={heading}
              isPickup={isPickup}
              order={order}
              now={now}
              ticks={ticks}
              isMarkingAll={isMarkingAll}
              onToggleLine={(line, next) => void setServed(order.id ?? 0, line, next)}
              onServeAll={() => void markAllServed(order.id ?? 0, order.items ?? [])}
              canEdit={mayEditOrder}
              // §9.319 - a counter collection has no table to add to, so the link carries the channel
              // and the order alone. Sending `table=Osobný odber` would invent a table of that name.
              onAdd={() =>
                navigate(
                  isPickup
                    ? `/new-order?type=bar&edit=${order.id}`
                    : `/new-order?type=restaurant&table=${encodeURIComponent(heading)}&edit=${order.id}`,
                )
              }
              onPay={() => setPayingId(order.id ?? null)}
              onCancel={() => setCancellingId(order.id ?? null)}
              onStornoed={refresh}
            />
          ))}
        </div>
      )}

      {paying && (
        <PaymentDialog
          order={paying}
          // §9.740 - the till asks who took the money, where the channel needs it.
          closer="asked"
          onClose={() => setPayingId(null)}
          onPaid={refresh}
          /* §9.464 - the dialog rewrote the order's own zľava, so the board is stale. The same
           * `refresh` the close uses: this screen reads its orders from one query. */
          onOrderChanged={refresh}
        />
      )}

      {cancelling && (
        <CancelOrderModal
          order={cancelling}
          askCredentials={credentialsNeeded(user?.systemRole ?? '', 'cancelled', hasPermission)}
          // §9.344 - a waiter closing a table is still asked, because `cancelled` is not theirs by
          // role; an administrator is not, because their session already proves it.
          onClose={() => setCancellingId(null)}
          onConfirm={async (email, password, reason) => {
            try {
              await cancelOrder(email, password, reason)
            } catch (caught) {
              throw new Error(apiErrorMessage(caught, tt('Neplatné prihlasovacie údaje')), {
                cause: caught,
              })
            }
          }}
        />
      )}
      {/* §9.444, FB-767 - the same sheet the floor plan opens, so a waiter never has to leave this
        * screen to close their shift. */}
      {showHandover && user?.id != null && (
        <WaiterHandoverModal
          waiterId={user.id}
          waiterName={user.name ?? ''}
          onClose={() => setShowHandover(false)}
          onSubmitted={() =>
            toast({ title: tt('Tržba odovzdaná'), description: tt('Report bol uložený.') })
          }
        />
      )}

    </div>
  )
}

function TableCard({
  tableName,
  isPickup,
  order,
  now,
  ticks,
  isMarkingAll,
  onToggleLine,
  onServeAll,
  canEdit,
  onAdd,
  onPay,
  onCancel,
  onStornoed,
}: {
  tableName: string
  /** §9.319 - a counter collection, which names itself rather than a table. */
  isPickup: boolean
  order: OrderListItem
  /** The page's clock, ticked with the list — see `openFor` below. */
  now: number
  /** Ticks in flight, shared with every other card — §9.127. */
  ticks: Record<number, PendingTick>
  isMarkingAll: boolean
  onToggleLine: (line: ServableLine & { quantity?: number; productName?: string }, next: string) => void
  onServeAll: () => void
  /**
   * §9.744 - `edit_order` and `new_order`, the rights *Upraviť* asks (`mayOpenOrderEditor`). Required, so a card cannot
   * be drawn without the answer.
   */
  canEdit: boolean
  onAdd: () => void
  onPay: () => void
  onCancel: () => void
  /** §9.442 - the list is re-read after a fiscal reversal, so the card stops offering it. */
  onStornoed: () => void
}) {
  const lineCount = order.items?.length ?? 0
  // §9.126 — read once per render from a clock the page ticks, never `Date.now()` inside the JSX:
  // an impure call during render is exactly what the lint rule is there to stop.
  const openFor = openForNote(order.createdAt, now)
  const lines = order.items ?? []
  const everythingServed = allServed(lines, ticks)

  // §9.443, FB-768 - the kitchen has finished and somebody has to carry it out.
  const waitingToCarry = isWaitingToBeCarried(order.status, lines, ticks)

  return (
    <div
      data-table-waiting={waitingToCarry ? 'yes' : 'no'}
      className={cn(
        'bg-card border rounded-2xl overflow-hidden flex flex-col',
        // The owner asked for *glowing, like blinking*. `animate-pulse` with a ring is the house
        // idiom - `DispatchPage` pulses its print button the same way - rather than a second
        // animation vocabulary for one card.
        waitingToCarry
          ? 'border-amber-400 ring-2 ring-amber-400/60 animate-pulse'
          : 'border-border',
      )}
    >
      <div
        className="px-4 py-3 border-b border-border flex items-center gap-2"
        data-open-card={isPickup ? 'pickup' : 'table'}
      >
        {isPickup && <ShoppingBag className="w-4 h-4 shrink-0 text-primary" />}
        <span className="text-lg font-black">{tableName}</span>
        <StatusBadge color={orderStatusColor(order.status)} label={orderStatusLabel(order.status)} />
        <span className="ml-auto text-lg font-black text-primary tabular-nums">
          {formatMoney(order.total)}
        </span>
      </div>

      <div className="px-4 py-2.5 flex-1 space-y-1">
        {/* §9.126 - the number and the time the card never said. The number is how this bill is
          * found on every other screen; the time is how a waiter knows which table has been sitting. */}
        <p className="text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
          <span className="font-mono font-bold text-primary" data-open-order-number>
            #{order.orderNumber}
          </span>
          {openFor !== '' && <span data-open-for>· {openFor}</span>}
          <span>
            · <Users className="w-3 h-3 inline" /> {lineCount} {tt('pol.')}
            {order.employeeName ? ` · ${order.employeeName}` : ''}
          </span>
        </p>
        {/* §9.375, FB-669 - the note. **A missed port**: legacy's open-table card draws it with a
          * 📝 (`restaurant.tsx:354`) and AGOS drew the table, the lines and the total without it, so
          * *bez cibule* reached the kitchen ticket and never the person carrying the plate.
          * `whitespace-pre-line` because the owner writes these on two lines and means two lines. */}
        {noteOf(order) !== '' && (
          <p
            className="text-[11px] text-amber-600 dark:text-amber-400 flex items-start gap-1 whitespace-pre-line"
            data-open-order-note
          >
            <span className="shrink-0">📝</span>
            {noteOf(order)}
          </p>
        )}
        {/* §9.127 - the lines, each one strikeable once it has been carried out. This was a joined
          * string clamped to two rows: on a bill of any size most of the order was not even visible,
          * and none of it was clickable. */}
        {lines.length === 0 ? (
          <p className="text-xs text-muted-foreground">-</p>
        ) : (
          <ul className="space-y-px" data-served-lines>
            {lines.map((line) => {
              const state = effectiveServed(line, ticks)
              const isServed = state === SERVED
              return (
                <li key={line.id ?? `${line.productName}`}>
                  <button
                    type="button"
                    data-served-line={line.id}
                    data-served={isServed ? 'yes' : 'no'}
                    onClick={() => void onToggleLine(line, toggledFrom(state))}
                    className={cn(
                      'w-full flex items-center gap-2 px-1.5 py-1 rounded-lg text-left text-xs transition-colors',
                      isServed
                        ? 'text-muted-foreground line-through bg-emerald-500/5 hover:bg-emerald-500/10'
                        : 'hover:bg-muted/50',
                    )}
                  >
                    <span
                      className={cn(
                        'w-4 h-4 rounded-full border flex items-center justify-center shrink-0',
                        isServed
                          ? 'bg-emerald-500 border-emerald-500 text-white'
                          : 'border-border bg-background text-muted-foreground',
                      )}
                    >
                      {isServed ? <CheckCheck className="w-2.5 h-2.5" /> : <CircleDot className="w-2.5 h-2.5" />}
                    </span>
                    <span className="truncate">
                      {line.quantity}× {line.productName}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
        {lines.length > 0 && !everythingServed && (
          <button
            type="button"
            data-serve-all
            disabled={isMarkingAll}
            onClick={() => void onServeAll()}
            className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 hover:underline disabled:opacity-50"
          >
            <T>Vyniesť všetko</T>
          </button>
        )}
        {/* A receipt is printed per payment. Adding to a table that already has
            one means the printed one no longer describes the sale. */}
        {needsReceiptStorno(order) && (
          <p className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold">
            <T>Doklad už bol vytlačený - zmena vyžaduje storno.</T>
          </p>
        )}
      </div>

      {/* §9.744 - two columns when *Upraviť* is not drawn, so the row does not end on an empty cell. */}
      <div className={cn('grid gap-px bg-border', canEdit ? 'grid-cols-3' : 'grid-cols-2')}>
        {/* §9.126 - it opens the whole order for editing, which is what legacy's own
            title says. *Pridať* named one of the things it does and hid the rest.
            §9.744 - only for somebody holding `edit_order` and `new_order`: the editor it opens cannot be opened
            without the second or save without the first. */}
        {canEdit && <CardAction icon={Pencil} label={tt('Upraviť')} onClick={onAdd} marker="edit" />}
        <CardAction
          icon={Receipt}
          label={tt('Zaplatiť')}
          onClick={onPay}
          className="text-emerald-600 dark:text-emerald-400"
        />
        <CardAction
          icon={Ban}
          label={tt('Storno')}
          onClick={onCancel}
          className="text-red-500"
        />
        {/* §9.442, FB-761 - the owner asked for it "in otvorené stoly next to Storno". It renders
          * nothing unless a fiscal document exists and nobody has reversed it, which is their
          * second message: hide it rather than disable it. The className matches `CardAction`'s so
          * it reads as one of the row rather than as a bolted-on button. */}
        <FiskalStornoAction
          order={order}
          onDone={onStornoed}
          compact
          label={tt('Storno fiškál')}
          className="flex items-center justify-center gap-1.5 py-2.5 bg-card text-xs font-bold text-red-500 hover:bg-muted transition-colors"
        />
      </div>
    </div>
  )
}

function CardAction({
  icon: Icon,
  label,
  onClick,
  className,
  marker,
}: {
  icon: typeof Plus
  label: string
  onClick: () => void
  className?: string
  /** §9.744 - `data-card-action`, so a test can name the action rather than its translated label. */
  marker?: string
}) {
  return (
    <button
      type="button"
      data-card-action={marker}
      onClick={onClick}
      className={cn(
        'flex items-center justify-center gap-1.5 py-2.5 bg-card text-xs font-bold hover:bg-muted transition-colors',
        className,
      )}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  )
}
